import type { FieldDescriptor } from "./service";

/**
 * Rank a cached iconik field catalog for one source column, filtered by the typed query.
 *
 * iconik lets two distinct fields share the same visible label (e.g. underlying names `Sport` and
 * `Sport2` both labelled "Sport"), so precedence is by SPECIFICITY of the match, strongest first:
 *   1. exact underlying field-NAME/key match to the source column
 *   2. exact visible-LABEL match
 *   3. name/label prefix of the column
 *   4. name/label prefix of the typed query
 *   5. any other case-insensitive substring match
 * This is exact-name SUGGESTION only; the operator still explicitly selects the field, and nothing
 * here authorizes a write (no fuzzy auto-mapping).
 */
export function rankFields(
  catalog: FieldDescriptor[],
  column: string,
  q: string
): FieldDescriptor[] {
  const nq = q.trim().toLowerCase();
  const nc = column.trim().toLowerCase();
  const matches = nq
    ? catalog.filter(
        (f) =>
          f.name.toLowerCase().includes(nq) ||
          f.label.toLowerCase().includes(nq)
      )
    : catalog;
  const score = (f: FieldDescriptor): number => {
    const n = f.name.toLowerCase();
    const l = f.label.toLowerCase();
    if (n === nc) {
      return 0;
    } // exact underlying field-name match — strongest
    if (l === nc) {
      return 1;
    } // exact visible-label match
    if (n.startsWith(nc) || l.startsWith(nc)) {
      return 2;
    }
    if (nq && (n.startsWith(nq) || l.startsWith(nq))) {
      return 3;
    }
    return 4;
  };
  return [...matches].sort(
    (a, b) =>
      score(a) - score(b) ||
      a.label.localeCompare(b.label) ||
      a.name.localeCompare(b.name)
  );
}

/**
 * How strongly a field matches the source column, for the picker's badge treatment:
 *   "name"  → underlying key is exactly the column (strongest — gets the "Exact match" badge)
 *   "label" → only the visible label matches (a weaker, unbadged match; disambiguated by its key)
 *   null    → not an exact match
 */
export function matchStrength(
  f: FieldDescriptor,
  column: string
): "name" | "label" | null {
  const nc = column.trim().toLowerCase();
  if (f.name.toLowerCase() === nc) {
    return "name";
  }
  if (f.label.toLowerCase() === nc) {
    return "label";
  }
  return null;
}

export function isExactColumnMatch(
  f: FieldDescriptor,
  column: string
): boolean {
  return matchStrength(f, column) !== null;
}
